import React, { MutableRefObject, useCallback, useEffect, useState } from 'react';
import '@inovua/reactdatagrid-community/index.css'
import ReactDataGrid from '@inovua/reactdatagrid-community'
import { TypeEditInfo } from '@inovua/reactdatagrid-community/types'
import SelectEditor from "@inovua/reactdatagrid-community/SelectEditor";
import Button from "../../../../components/button/Button";
import "./bulkUsersImportTable.css";
import styles from "./bulkUsersImportTable.module.scss";
import Checkbox from "../../../../components/checkbox/Checkbox";
import { gql, useLazyQuery } from "@apollo/client";
import { Dictionary } from "../../../../types";
import { isNotEmail } from "../../../../utils/validation";
import { useToast } from "../../../../hooks/useToast";
import { isOdd } from "../../../../utils/helpers";
import { TypeComputedProps } from "@inovua/reactdatagrid-community/types";
import Spinner from "../../../../components/spinner/Spinner";
import { useDistrictAdminInviteUsersMutation } from '@src/api/graphql-codegen';
import { FixDataWizard } from './fixDataWizard';
import { useUser } from '@src/hooks';
import { logger } from '../../../../utils/logger';

export type jsonParseDataType = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  account?: string;
  adminEmail: any[] | string;
  school?: string;
  role: string;
  invite: string;
}

type Props = {
  jsonParse: jsonParseDataType[];
  accountOptions: any;
  schoolOptions: any;
  closeSlide: any
};

type AdminUsersResponse = {
  adminUsersByBulk: any;
  adminUsers: Dictionary[];
}

const queryAdminUsersByBulk = gql`
  query AdminUsersByBulk($schoolIds: [ID!]) {
    adminUsersByBulk(schoolIds: $schoolIds) {
      id
      result {
          id
          name
      }
    }
  }
`;

export type emailChecking = {
  rowId: number;
  column: string;
  fieldValue: string;
}

export type nameChecking = {
  rowId: number;
  column: string;
  fieldValue: string;
}

export type adminChecking = {
  rowId: number;
  column: string;
  fieldValue: string;
}

export type adminEmailChecking = {
  rowId: number;
  column: string;
  fieldValue?: string;
  otherData?: any[];
  name: string;
};

// type dropAdminArrType = {
//   id: string;
//   label: string;
// }

// type dropAdminType = {
//   [key: number]: dropAdminArrType[]
// }

export type typoCheckingDataToClean = {
  checkingEmails: emailChecking[]
  checkingAdmins: adminEmailChecking[]
  checkingFirstName: nameChecking[]
  checkingSecondName: nameChecking[]
  checkingSchools: nameChecking[]
}

let tableCSS = {
  firstName: {
    width: 140
  },
  secondName: {
    width: 140
  },
  email: {
    width: 140
  },
  adminEmail: {
    width: 200
  },
  school: {
    width: 175
  },
  account: {
    width: 175
  },
  role: {
    width: 120
  },
  remove: {
    width: 90
  }
}

const accountTypes = [
  {id: "Educator", label: "Educator"},
  {id: "Admin", label: "Admin"}];

// @ts-ignore
const rowClassName = ({data}) => (isOdd(data?.id)) ? 'odd-rows' : '';

let gridStyle = {
  maxHeight: 540,
  minHeight: 540
}


let stringCharExists = (value: string | undefined) => {
  return !(!value || value?.length === 0 || !value?.trim());
}

export const BulkUsersImportDistrictAdminTable = (props: Props) => {
    const {jsonParse: uploadedData, accountOptions, schoolOptions, closeSlide} = props;

    const [user] = useUser();

    const [gridRef, setGridRef] = useState<MutableRefObject<TypeComputedProps | null> | null>(null);
    const [flagToClean, setFlagToClean] = useState<boolean | null>(null); // change to false
    const [dataSource, setDataSource] = useState<jsonParseDataType[]>([]);
    const [dataToClean, setDataToClean] = useState<typoCheckingDataToClean>({
      checkingFirstName: [],
      checkingSecondName: [],
      checkingEmails: [],
      checkingAdmins: [],
      checkingSchools: [],
    });
    
    const [success, setSuccess] = useState(false);
    const [duplicateEmailMessage, setDuplicateEmailMessage] = useState(false);
    const {successToast, errorToast} = useToast();
    const [dropDownSchool, setDropDownSchool] = useState<any[]>([]);

    useEffect(() => {
      const downs: { id: string, label: string }[] = (user?.schools ?? []).map((school) => {
        return {
          id: school?.name ?? '',
          label: school?.name ?? ''
        }
      });
      setDropDownSchool(downs);
    }, [user?.schools])

    const [getAdminUsersByBulk] = useLazyQuery<AdminUsersResponse>(queryAdminUsersByBulk, {});

    const [bulkInviteUsers, {loading: bulkInviteProcessing}] = useDistrictAdminInviteUsersMutation({
      onError(err) {
        errorToast('Bulk upload invite failed, please check the developers console and contact customer support!');
        logger.info(err);
      },
    })

    const bulkUploadColumns: any[] = [
      {
        name: 'firstName', header: 'First Name * ', width: tableCSS.firstName.width, headerEllipsis: false,
        headerProps: {
          className: "table-header"
        },
        render: ({value}) => (<span style={{marginLeft: 20, fontSize: 14}}>{value}</span>),
      },
      {
        name: 'lastName', header: 'Second Name *', width: tableCSS.secondName.width, headerEllipsis: false,
        headerProps: {
          className: "table-header"
        },
        render: ({value}) => (<span style={{marginLeft: 20, fontSize: 14}}>{value}</span>),
      },
      {
        name: 'email', header: 'Email *', minWidth: tableCSS.email.width, flex: 1,
        headerProps: {
          className: "table-header"
        },
        render: ({value}) => (<span style={{marginLeft: 20, fontSize: 14}}>{value}</span>),
      },
      {
        name: 'school',
        header: 'School',
        editor: SelectEditor,
        onClick: (e: any) => {
          let clickEvent = new Event("dblclick", {"bubbles": true, "cancelable": false});
          e.target.dispatchEvent(clickEvent);
          setTimeout(() => {
            try {
              if (e.target.parentNode.childNodes[1].firstElementChild.childNodes[1]) e.target.parentNode.childNodes[1].firstElementChild.childNodes[1].click();
              if (e.target.childNodes[1].childNodes[0].childNodes[1]) e.target.childNodes[1].childNodes[0].childNodes[1].click();
            } catch (e) {
            }
          }, 100)
        },
        editorProps: {
          idProperty: "id",
          onClick: () => logger.info('clicked'),
          dataSource: dropDownSchool,
          collapseOnSelect: true,
          clearIcon: null,
        },
        flex: 1,
        width: 900,
        // @ts-ignore
        render: ({data}: { data: any }) => <div className={`school-${data?.id}`}
                                                id={`school-${data?.id}`} style={{marginLeft: 20, fontSize: 14}}>{data?.school}</div>,
        headerProps: {
          className: "table-header"
        }
      },
      {
        name: 'role', header: 'Role *', width: tableCSS.role.width,
        editor: SelectEditor,
        editorProps: {
          idProperty: "id",
          dataSource: accountTypes,
          collapseOnSelect: true,
          clearIcon: null,
        },
        headerProps: {
          className: "table-header"
        },
        render: ({value}) => (<span style={{display: 'flex', justifyContent: 'center', textAlign: 'center', fontSize: 14}} >{value}</span>),
      },
      {
        name: 'invite',
        header: <span title={"Confirm your invite"}>Invite *</span>,
        headerEllipsis: false,
        editable: true,
        // @ts-ignore
        render: ({value, data}) => {
          let checked = true;
          if (value === "No") checked = false;
          return <div
            title={"If you can't select this box, please make sure this email is unique and that all fields are filled."}
            style={{display: 'flex', justifyContent: 'center', textAlign: 'center', fontSize: 14}}
            >
            <Checkbox
              id={value} checked={checked}
              onChange={(b) => updateTableDataField(b ? "Yes" : "No", data?.id, "invite")}/></div>
        },
        width: tableCSS.remove.width,
        headerProps: {
          className: "table-header"
        }
      }
    ]

    useEffect(() => {
      let ifStrings = dataSource.some(item => typeof item.adminEmail === "string");
      if (dataSource && dataSource.length > 0 && ifStrings) {
        let newDataSource = dataSource.map(strings => {
          if (typeof strings.adminEmail === "string") {
            strings.adminEmail = [strings.adminEmail];
            // @ts-ignore
            strings.adminEmail.getID = () => dataSource[strings.id].id;
          }
          return strings;
        });
        setDataSource(newDataSource);
      }

      let emailList: string[] = [];
      let uniqueEmailList: string[] = [];
      let difference: boolean = false;

      dataSource.forEach((invitee) => emailList.push(invitee.email));

      // Todo - fix, so when no more duplicate email error, hide error message and also, make it so any email field can be deselected, not just the first one in order
      let newDataSource: jsonParseDataType[] = dataSource.map((invitee) => {
        if (flagToClean === false && (invitee.firstName === "" || invitee.lastName === "" || invitee.email === "")) {
          invitee.invite = "No";
        }

        if (invitee.email === "") {
        } else if (uniqueEmailList.indexOf(invitee.email) === -1) {
          uniqueEmailList.push(invitee.email);
        } else if (invitee.invite === "Yes") {
          invitee.invite = "No";
          difference = true;
        }
        return invitee;
      });
      if (difference) {
        setDataSource(newDataSource);
        setDuplicateEmailMessage(true);
      }
    }, [dataSource, flagToClean])
    useEffect(() => {
      if (uploadedData.length > 0) setDataSource(uploadedData);

      // todo - prefill admin email column
      if (uploadedData.length > 0 && accountOptions.length > 0 && schoolOptions.length > 0) {
        let dirty = false;
        const fn: nameChecking[] = [];
        const sn: nameChecking[] = [];
        const e: emailChecking[] = [];
        const a: adminEmailChecking[] = [];
        const s: any[] = []; // school array

        uploadedData.forEach((dataElement) => {
          if (dataElement?.school) {
            const getSchoolName = schoolOptions.find((item: any) => item?.label && (item?.label).trim() === (dataElement.school || '').trim());
            s.push(getSchoolName?.value);
          }

          if (!dataElement?.firstName || dataElement?.firstName?.length === 0 || !dataElement?.firstName.trim?.()) {
            fn.push({
              rowId: dataElement?.id,
              column: "firstName",
              fieldValue: dataElement?.firstName,
            });
            dirty = true;
          }

          if (!dataElement?.lastName || dataElement?.lastName?.length === 0 || !dataElement?.lastName?.trim?.()) {
            sn.push({
              rowId: dataElement?.id,
              column: "lastName",
              fieldValue: dataElement?.lastName,
            });
            dirty = true;
          }

          if (dataElement?.email || (!dataElement?.email && (dataElement?.lastName || dataElement?.firstName))) {
            if (isNotEmail(dataElement?.email)) {
              e.push({
                rowId: dataElement?.id,
                column: "email",
                fieldValue: dataElement?.email,
              });
              dirty = true;
            }
          }

        });
        if (dirty) {
          setDataToClean({
            checkingFirstName: fn ?? [],
            checkingSecondName: sn ?? [],
            checkingEmails: e ?? [],
            checkingAdmins: a ?? [],
            checkingSchools: s ?? []
          });
        }
        setFlagToClean(dirty);
      }
    }, [uploadedData, accountOptions.length, getAdminUsersByBulk, schoolOptions]);

    const onEditComplete = useCallback(({value, columnId, rowId}: TypeEditInfo) => {
      const data: (jsonParseDataType)[] = [...dataSource];
      // best practice
      // 1. check if rowId < data.length
      // 2. columnId is property of jsonParseDataType
      if ((columnId === "firstName" || columnId === "lastName") && dataSource[rowId].invite === "Yes") {
        if (!stringCharExists(value)) {
          // @ts-ignore
          gridRef?.current.startEdit({rowIndex: rowId, columnId: columnId});
          errorToast(`Value cannot be empty, please fix it.`);
          return
        }
      }

      if (columnId === "email" && dataSource[rowId].invite === "Yes") {
        if (isNotEmail(value)) {
          // @ts-ignore
          gridRef?.current.startEdit({rowIndex: rowId, columnId: columnId});
          errorToast(`${value} is not a valid email address, please fix it.`);
          return;
        }
      }
      // @ts-ignore
      data[rowId][columnId] = value;
      // @ts-ignore
      setDataSource(data);
    }, [dataSource, errorToast, gridRef]);

    const updateTableDataField = (value: any, rowId: number, columnId: string) => {
      const data = [...dataSource];
      // @ts-ignore
      data[rowId][columnId] = value;
      // @ts-ignore
      setDataSource(data);
    }

    const inviteUsers = useCallback(async () => {
      const invitesToSend: any[] = [];
      let send: boolean = false;
      dataSource.forEach((invitee) => {
        if (invitee.invite === "Yes") {
          let admin = false;
          let accountId = "";
          let schoolId = "";
          const adminId = user?.id;
          const adminName = `${user?.firstName} ${user?.lastName}`
          if (invitee.role === "Admin") admin = true;

          schoolId = schoolOptions.find((school: any) => school.label && school.label?.trim() === invitee.school?.trim()).value;
          accountId = accountOptions.find((account: any) => account.label && account.label?.trim() === invitee.account?.trim()).value;

          invitesToSend.push({
            firstName: invitee.firstName.toString(),
            lastName: invitee.lastName.toString(),
            email: invitee.email,
            admin: admin,
            adminId: adminId,
            customer: accountId,
            school: schoolId,
            schoolName: invitee.school?.trim() || '',
            adminName: adminName
          })
          send = true;
        }
      });

      if (send) {
        try {
          const CHUNK_LIMIT_LENGTH = 15;
          if (invitesToSend.length && invitesToSend.length > CHUNK_LIMIT_LENGTH) {
            const chunk: any[][] = [];
            for (let i = 0; i < invitesToSend.length; i += CHUNK_LIMIT_LENGTH) {
              chunk.push(invitesToSend.slice(i, i + CHUNK_LIMIT_LENGTH));
            }
            const res = await Promise.all(chunk.map(invites => bulkInviteUsers({variables: {input: invites}})));
            if (res.length === chunk.length) {
              setSuccess(true);
              successToast('Your bulk contact invites have been successfully sent!');
            }
          } else {
            const res = await bulkInviteUsers({variables: {input: invitesToSend}});
            if (res?.data?.districtAdminInviteUsers?.length) {
              setSuccess(true);
              successToast('Your bulk contact invites have been successfully sent!');
            }
          }
        } catch (e: any) {
          errorToast(e?.message || '');
        };
      }
    }, [accountOptions, bulkInviteUsers, dataSource, successToast, errorToast, schoolOptions, user]);

    if (flagToClean === null) {
      return (
        <>
          <div style={{marginTop: 20, marginBottom: 50}}>Checking Validation...</div>
          <Spinner/>
        </>
      );
    }

    const closeAndClean = () => {
      setFlagToClean(null);
      setDataSource([]);
      setDataToClean({
        checkingEmails: [],
        checkingAdmins: [],
        checkingSecondName: [],
        checkingFirstName: [],
        checkingSchools: [],
      });
      // setAdminData([]);
      setSuccess(false);
      closeSlide();
    }

    return (
      <>
        {flagToClean && !success ?
          <h3 className={styles.bulkInviteSubTitle} id="data-cleaning">Step 3: We will now
            guide
            you through cleaning of your data.</h3>
          : success ?
            <h3 className={styles.bulkInviteSubTitle}>Step 5: We have completed your Bulk
              Contact Invitation </h3> :
            <h3 className={styles.bulkInviteSubTitle}>Step 4: Send Bulk Invite</h3>
        }
        {!success && !bulkInviteProcessing &&
        <div className={styles.noteText} style={{marginBottom: 10}}>(Optional) Double click to edit fields
        </div>}
        {flagToClean && (dataToClean.checkingEmails.length > 0 || dataToClean.checkingAdmins.length > 0) && !success &&
        <FixDataWizard
          updateTableDataField={updateTableDataField}
          dataSource={dataSource}
          dataToClean={dataToClean}
          setTourEnd={setFlagToClean}
        />
        }
        {flagToClean ? (
          <div className={styles.table}>
            <div className={styles.header}>
              <div style={{width: tableCSS.firstName.width}}>First Name *</div>
              <div style={{width: tableCSS.secondName.width}}>Second Name *</div>
              <div style={{minWidth: tableCSS.email.width, flex: 1}}>Email *</div>
              <div style={{minWidth: tableCSS.school.width, flex: 1}}>School</div>
              <div style={{minWidth: tableCSS.role.width}}>Role *</div>
              <div style={{width: tableCSS.remove.width}}>Invite *</div>
            </div>
            <div className={styles.tableBody} id={"bulkInviteTable"}>
              {
                dataSource.map((x, i) => (
                    <div key={i} className={styles.rows}>
                      <div
                        className={`firstName-${x.id}`}
                        id={`firstName-${x.id}`}
                        style={{width: tableCSS.firstName.width}}
                      >
                        <span>{x.firstName}</span>
                      </div>
                      <div className={`lastName-${x.id}`} id={`lastName-${x.id}`}
                           style={{width: tableCSS.secondName.width}}><span>{x.lastName}</span>
                      </div>
                      <div className={`email-${x.id}`} id={`email-${x.id}`}
                           style={{minWidth: tableCSS.email.width, flex: 1}}><span>{x.email}</span>
                      </div>
                      <div className={`${styles.col7} school-${x.id}`} id={`school-${x.id}`}
                           style={{minWidth: tableCSS.school.width, flex: 1}}><span>{x.school}</span>
                      </div>
                      <div style={{minWidth: tableCSS.role.width}}><span>{x.role}</span></div>
                      <div style={{width: tableCSS.remove.width}}><span>{x.invite}</span></div>
                    </div>
                  )
                )
              }
            </div>
          </div>
        ) : (
          <div style={{position: "relative"}}>
            {(success || bulkInviteProcessing) &&
            <div style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255,255,255, 0.8)",
              zIndex: 1,
              textAlign: "center"
            }}>
             <div style={{
               position: "absolute",
               top: "50%",
               left: "50%",
               transform: "translate(-50%, -50%)",
               fontSize: "24px",
             }}>{success && "Bulk Upload Completed!"}{bulkInviteProcessing && (
               <div>
                 <div style={{marginTop: 20, marginBottom: 50}}>Bulk Uploading Data... Please wait.</div>
                 <Spinner/>
               </div>
             )}
             </div>
            </div>}
            <ReactDataGrid
              onReady={(gridData) => {
                setGridRef(gridData);
              }}
              reorderColumns={false}
              showColumnMenuTool={false}
              rowClassName={rowClassName}
              sortable={false}
              headerHeight={60}
              idProperty="id"
              pagination={false}
              style={gridStyle}
              onEditComplete={onEditComplete}
              editable={true}
              columns={bulkUploadColumns}
              dataSource={dataSource}
              activateRowOnFocus allowGroupSplitOnReorder allowUnsort autoCheckboxColumn
              autoFocusOnEditComplete
              cellSelectionByIndex checkboxOnlyRowSelect checkboxSelectEnableShiftKey checkResizeDelay={0}
            />
          </div>
        )
        }
        {duplicateEmailMessage && !success && !bulkInviteProcessing &&
        <div style={{color: "red", marginTop: 20}}><span style={{fontWeight: 600}}>Please note:</span> You have
         duplicate email address invites. You
         will not be able to select the "Invite"
         checkbox on the right and invite the same email address twice.</div>}
        <div style={{width: "100%", marginTop: "20px"}}>
          {success ? <Button
              color={"green"}
              onClick={closeAndClean}
              className={styles.inviteBtn}
              label={`Close Bulk Upload`}/> :
            <div style={{display: "flex"}}>
              <Button
                color={"red"}
                disabled={bulkInviteProcessing}
                onClick={closeAndClean}
                className={styles.inviteBtn}
                label={bulkInviteProcessing ? '' : `CANCEL BULK UPLOAD`}/>
              <Button
                disabled={flagToClean || dataSource.length === 0 || bulkInviteProcessing || !dataSource.some(invitee => invitee.invite === "Yes")}
                onClick={inviteUsers}
                className={styles.inviteBtnRight}
                label={bulkInviteProcessing ? '' : `Invite ${dataSource.filter((invitees: any) => invitees.invite === "Yes").length} Users`}/>
            </div>}
        </div>
      </>
    );
  }
;